import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useState, useEffect } from "react";
import { getRedirectUrl } from "../lib/auth";
import type { User } from "@supabase/supabase-js";
import {
  ArrowRight,
  Play,
  Sparkles,
  Zap,
  Users,
  Check,
  Menu,
  X,
} from "lucide-react";

export default function Landing() {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setUser(session?.user ?? null);
      },
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const handleSignIn = async (provider: "google" | "github") => {
    setIsSigningIn(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: getRedirectUrl("/generate"),
        },
      });
      if (error) throw error;
    } catch (error) {
      console.error("Error signing in:", error);
      setIsSigningIn(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen w-full bg-[#020617] relative text-foreground">
      {/* Dark Sphere Grid Background */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background: "#020617",
          backgroundImage: `
            linear-gradient(to right, rgba(71,85,105,0.3) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(71,85,105,0.3) 1px, transparent 1px),
            radial-gradient(circle at 50% 50%, rgba(139,92,246,0.15) 0%, transparent 70%)
          `,
          backgroundSize: "32px 32px, 32px 32px, 100% 100%",
        }}
      />
      
      <div className="relative z-10">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-background/80 backdrop-blur-md z-50 border-b border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => scrollToSection("hero")}
                className="text-xl font-semibold text-foreground hover:text-primary transition-colors"
              >
                Bardus AI
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection("features")}
                className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
              >
                Pricing
              </button>
              {user ? (
                <Link
                  to="/generate"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Generate
                </Link>
              ) : (
                <button
                  onClick={() => handleSignIn("google")}
                  disabled={isSigningIn}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {isSigningIn ? "Signing in..." : "Sign In"}
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col space-y-4">
                <button
                  onClick={() => scrollToSection("features")}
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium text-left"
                >
                  Features
                </button>
                <button
                  onClick={() => scrollToSection("pricing")}
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium text-left"
                >
                  Pricing
                </button>
                {user ? (
                  <Link
                    to="/generate"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium transition-colors w-fit"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Generate
                  </Link>
                ) : (
                  <button
                    onClick={() => handleSignIn("google")}
                    disabled={isSigningIn}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 w-fit"
                  >
                    {isSigningIn ? "Signing in..." : "Sign In"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
            <Sparkles size={16} className="mr-2" />
            AI-Powered Sound Generation
          </div>

          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-foreground leading-tight">
            Create Perfect Sound Effects
            <span className="block text-primary">In Seconds</span>
          </h1>

          <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
            Generate high-quality, custom sound effects for your games using AI.
            No audio expertise required.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            {user ? (
              <Link
                to="/generate"
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-lg text-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Play size={20} />
                Start Creating
              </Link>
            ) : (
              <button
                onClick={() => handleSignIn("google")}
                disabled={isSigningIn}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-lg text-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Play size={20} />
                {isSigningIn ? "Signing in..." : "Start Free"}
              </button>
            )}
            <button
              onClick={() => scrollToSection("features")}
              className="border border-input hover:border-input/80 text-foreground px-8 py-4 rounded-lg text-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              Learn More
              <ArrowRight size={20} />
            </button>
          </div>

          {/* Demo Preview */}
          <div className="bg-muted/50 rounded-2xl p-8 max-w-2xl mx-auto">
            <div className="bg-card rounded-lg p-6 shadow-sm border border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <div className="text-left">
                <div className="text-sm text-muted-foreground mb-2">
                  Describe your sound:
                </div>
                <div className="bg-muted rounded-lg p-3 text-foreground font-mono text-sm">
                  "Epic sword clash with metallic ring"
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  Generated in 2.3 seconds
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything you need to create amazing audio
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Professional-quality sound effects powered by cutting-edge AI
              technology
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-card rounded-xl p-8 shadow-sm border border-border">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">
                Lightning Fast
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Generate professional sound effects in seconds. Perfect for
                rapid prototyping and tight deadlines.
              </p>
            </div>

            <div className="bg-card rounded-xl p-8 shadow-sm border border-border">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">
                AI-Powered
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Advanced neural networks trained on professional audio libraries
                create unique, high-quality sounds.
              </p>
            </div>

            <div className="bg-card rounded-xl p-8 shadow-sm border border-border">
              <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">
                Game Ready
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Optimized formats and file sizes perfect for Unity, Unreal
                Engine, Godot, and other game engines.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-muted-foreground">
              Start free, scale as you grow
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Free Plan */}
            <div className="bg-card rounded-xl p-8 shadow-sm border border-border">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  Free
                </h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-bold text-foreground">$0</span>
                  <span className="text-muted-foreground">forever</span>
                </div>
                <p className="text-muted-foreground">
                  Perfect for trying out the platform
                </p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-foreground">
                    10 sound generations per month
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-foreground">MP3 format</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-foreground">Personal use</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-foreground">Community support</span>
                </li>
              </ul>

              {user ? (
                <Link
                  to="/generate"
                  className="w-full bg-secondary hover:bg-secondary/80 text-secondary-foreground py-3 px-6 rounded-lg font-medium transition-colors text-center block"
                >
                  Get Started
                </Link>
              ) : (
                <button
                  onClick={() => handleSignIn("google")}
                  disabled={isSigningIn}
                  className="w-full bg-secondary hover:bg-secondary/80 text-secondary-foreground py-3 px-6 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Get Started
                </button>
              )}
            </div>

            {/* Pro Plan */}
            <div className="bg-primary rounded-xl p-8 shadow-lg text-primary-foreground relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-primary-foreground/20 text-primary-foreground text-sm px-3 py-1 rounded-full">
                  Most Popular
                </span>
              </div>

              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-2">Pro</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-bold">$19</span>
                  <span className="text-primary-foreground/80">per month</span>
                </div>
                <p className="text-primary-foreground/80">
                  For serious game developers
                </p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary-foreground" />
                  <span>500 sound generations per month</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary-foreground" />
                  <span>MP3 & WAV formats</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary-foreground" />
                  <span>Commercial use license</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary-foreground" />
                  <span>Priority support</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-primary-foreground" />
                  <span>Advanced parameters</span>
                </li>
              </ul>

              {user ? (
                <Link
                  to="/generate"
                  className="w-full bg-primary-foreground hover:bg-primary-foreground/90 text-primary py-3 px-6 rounded-lg font-medium transition-colors text-center block"
                >
                  Upgrade to Pro
                </Link>
              ) : (
                <button
                  onClick={() => handleSignIn("google")}
                  disabled={isSigningIn}
                  className="w-full bg-primary-foreground hover:bg-primary-foreground/90 text-primary py-3 px-6 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Start Pro Trial
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Ready to create amazing sound effects?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join thousands of game developers using AI to create custom audio
            assets.
          </p>
          {user ? (
            <Link
              to="/generate"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-lg text-lg font-medium transition-colors inline-flex items-center gap-2"
            >
              <Play size={20} />
              Start Creating
            </Link>
          ) : (
            <button
              onClick={() => handleSignIn("google")}
              disabled={isSigningIn}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-lg text-lg font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              <Play size={20} />
              Get Started Free
            </button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Bardus AI
              </h3>
              <p className="text-muted-foreground mb-4 max-w-md">
                AI-powered sound generation for game developers. Create
                professional sound effects in seconds.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">
                Product
              </h4>
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => scrollToSection("features")}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection("pricing")}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    Pricing
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">
                Support
              </h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/privacy"
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    Terms
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-border text-center">
            <p className="text-muted-foreground text-sm">
              &copy; 2024 Bardus AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}
